import { pickQuagganEmoji } from "../utils/quagganEmojiPool";

function PublishWheelSnapshot({
  snapshot,
  getNameIconSrc,
  showQuagganEmojis,
}) {
  const rawEntries = Array.isArray(snapshot?.entries) ? snapshot.entries : [];
  const names = Array.isArray(snapshot?.names) ? snapshot.names : [];
  const winnerIndex = Number.isInteger(snapshot?.winnerIndex)
    ? snapshot.winnerIndex
    : -1;
  const winnerEntryIndex = Number.isInteger(snapshot?.winnerEntryIndex)
    ? snapshot.winnerEntryIndex
    : -1;
  const nameEmojiSeeds = Array.isArray(snapshot?.nameEmojiSeeds)
    ? snapshot.nameEmojiSeeds
    : [];

  const fallbackSliceAngle = names.length ? 360 / names.length : 0;
  const entries = rawEntries.length
    ? rawEntries
    : names.map((name, idx) => ({
      id: `publish-slice-${idx}`,
      name,
      label: name,
      angleStart: idx * fallbackSliceAngle,
      angleSpan: fallbackSliceAngle,
      iconLookupName: name,
      emojiSeed: nameEmojiSeeds[idx] || `${name}-${idx}`,
    }));

  if (!entries.length) {
    return (
      <div
        style={{
          color: "rgba(190, 230, 255, 0.8)",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: "0.03em",
          textAlign: "center",
        }}
      >
        Preparing wheel preview...
      </div>
    );
  }

  const normalizedWinnerIndex =
    winnerEntryIndex >= 0 && winnerEntryIndex < entries.length
      ? winnerEntryIndex
      : winnerIndex >= 0 && winnerIndex < entries.length
        ? winnerIndex
        : 0;
  const winnerEntry = entries[normalizedWinnerIndex];
  const winnerRotation = winnerEntry
    ? 90 - winnerEntry.angleStart - winnerEntry.angleSpan / 2
    : 90;

  const getSliceTextProps = (name, currentSliceAngle, radius) => {
    const maxFontSize = 32;
    const minFontSize = 8;
    if (!name) return { fontSize: minFontSize, lines: [] };

    const words = name.split(" ");

    let fontSize = Math.min(
      maxFontSize,
      Math.max(
        minFontSize,
        (2 * Math.PI * radius * (currentSliceAngle / 360)) /
          Math.max(...words.map((word) => word.length * 0.6)),
      ),
    );

    const sliceAngleRad = (currentSliceAngle * Math.PI) / 180;
    const maxHeight = 2 * radius * Math.sin(sliceAngleRad / 2);
    const maxLines = Math.max(1, Math.floor(maxHeight / fontSize));

    const lines = [];
    let currentLine = "";
    const maxLineLen = 14;

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxLineLen) currentLine = testLine;
      else {
        lines.push(
          currentLine.length > maxLineLen
            ? `${currentLine.slice(0, maxLineLen - 1)}…`
            : currentLine,
        );
        currentLine = word;
      }
    }

    if (currentLine) {
      lines.push(
        currentLine.length > maxLineLen
          ? `${currentLine.slice(0, maxLineLen - 1)}…`
          : currentLine,
      );
    }

    if (lines.length > maxLines) {
      const truncated = lines.slice(0, maxLines);
      const last = truncated[maxLines - 1];
      truncated[maxLines - 1] =
        last.length > 3 ? `${last.slice(0, maxLineLen - 1)}…` : last;
      return { fontSize, lines: truncated };
    }

    return { fontSize, lines };
  };

  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 600 600"
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", margin: "0 auto" }}
    >
      <defs>
        <radialGradient id="publishWheelBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(20, 70, 120, 0.6)" />
          <stop offset="100%" stopColor="rgba(5, 25, 60, 0.75)" />
        </radialGradient>
        <filter
          id="publishGlowBlue"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <circle cx="300" cy="300" r="240" fill="url(#publishWheelBg)" />
      <circle
        cx="300"
        cy="300"
        r="240"
        fill="none"
        stroke="rgba(80, 180, 255, 0.45)"
        strokeWidth="3"
      />
      <circle
        cx="300"
        cy="300"
        r="236"
        fill="none"
        stroke="rgba(100, 200, 255, 0.15)"
        strokeWidth="1"
      />

      <polygon
        points="540,300 572,288 572,312"
        fill="#40c8ff"
        stroke="#0080c0"
        strokeWidth="2"
        style={{ filter: "drop-shadow(0 0 8px rgba(0, 180, 255, 0.9))" }}
      />

      <g
        style={{
          transform: `rotate(${winnerRotation}deg)`,
          transformOrigin: "300px 300px",
        }}
      >
        {entries.map((entry, idx) => {
          const angle = entry.angleStart;
          const sliceAngle = entry.angleSpan;
          const displayLabel = entry.label || entry.name || "";
          const iconLookupName = entry.iconLookupName || entry.name || "";
          const radians = ((angle + sliceAngle / 2 - 90) * Math.PI) / 180;
          const showClusterCount = Number(entry.count) > 1;
          const textRadius = showClusterCount ? 122 : 135;
          const x = 300 + textRadius * Math.cos(radians);
          const y = 300 + textRadius * Math.sin(radians);
          const radialAngle = angle + sliceAngle / 2 - 90;
          const normalizedScreenTextAngle =
            ((radialAngle + winnerRotation) % 360 + 360) % 360;
          const shouldFlipText =
            normalizedScreenTextAngle > 90 && normalizedScreenTextAngle < 270;
          const textRotation = radialAngle + (shouldFlipText ? 180 : 0);
          const isWinner = idx === normalizedWinnerIndex;
          const iconSrc = getNameIconSrc ? getNameIconSrc(iconLookupName) : "";
          const emojiSrc = iconSrc
            ? iconSrc
            : showQuagganEmojis
              ? pickQuagganEmoji({
                  type: "all",
                  seed: entry.emojiSeed || nameEmojiSeeds[idx] || `${displayLabel}-${idx}`,
                })
              : "";
          const emojiRadius = showClusterCount ? 204 : 190;
          const emojiSize = 40;
          const emojiX = 300 + emojiRadius * Math.cos(radians);
          const emojiY = 300 + emojiRadius * Math.sin(radians);
          const { fontSize: sliceFontSize, lines } = getSliceTextProps(
            displayLabel,
            sliceAngle,
            textRadius,
          );

          return (
            <g key={entry.id || `${displayLabel}-${idx}`}>
              {isWinner && (
                <path
                  d={`M300,300 L${300 + 240 * Math.cos(((angle - 90) * Math.PI) / 180)},${300 + 240 * Math.sin(((angle - 90) * Math.PI) / 180)} A240,240 0 ${sliceAngle > 180 ? 1 : 0},1 ${300 + 240 * Math.cos(((angle + sliceAngle - 90) * Math.PI) / 180)},${300 + 240 * Math.sin(((angle + sliceAngle - 90) * Math.PI) / 180)} Z`}
                  fill="rgba(60, 180, 255, 0.15)"
                />
              )}
              <line
                x1={300}
                y1={300}
                x2={300 + 240 * Math.cos(((angle - 90) * Math.PI) / 180)}
                y2={300 + 240 * Math.sin(((angle - 90) * Math.PI) / 180)}
                stroke="rgba(100, 200, 255, 0.25)"
                strokeWidth="1"
              />
              {emojiSrc ? (
                <image
                  href={emojiSrc}
                  xlinkHref={emojiSrc}
                  x={emojiX - emojiSize / 2}
                  y={emojiY - emojiSize / 2}
                  width={emojiSize}
                  height={emojiSize}
                  preserveAspectRatio="xMidYMid meet"
                  transform={`rotate(${-winnerRotation} ${emojiX} ${emojiY})`}
                />
              ) : null}
              <text
                x={x}
                y={
                  y -
                  ((lines.length - 1) / 2) * sliceFontSize +
                  sliceFontSize * 0.2
                }
                fill={isWinner ? "#40e8ff" : "rgba(200, 235, 255, 0.9)"}
                fontSize={sliceFontSize}
                textAnchor="middle"
                alignmentBaseline="middle"
                transform={`rotate(${textRotation} ${x} ${y})`}
                filter={isWinner ? "url(#publishGlowBlue)" : undefined}
                style={isWinner ? { fontWeight: "bold" } : {}}
              >
                {lines.map((line, lineIndex) => (
                  <tspan
                    key={`${displayLabel}-${lineIndex}`}
                    x={x}
                    dy={lineIndex === 0 ? 0 : sliceFontSize}
                  >
                    {line}
                  </tspan>
                ))}
                {showClusterCount ? (
                  <tspan
                    x={x}
                    dy={lines.length ? Math.max(8, Math.min(14, sliceFontSize * 0.58)) : 0}
                    fill={isWinner ? "#9af7ff" : "rgba(205, 238, 255, 0.95)"}
                    fontSize={Math.max(10, Math.min(14, sliceFontSize * 0.68))}
                    style={{ fontWeight: 800 }}
                  >
                    x{entry.count}
                  </tspan>
                ) : null}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default PublishWheelSnapshot;